import React, { useState } from "react";
import { getBackendUrl } from "../config";

/**
 * Écran d'enrôlement du facteur local — POST /api/auth/password/bootstrap
 *
 * POURQUOI CET ÉCRAN EXISTE
 * -------------------------
 * La voie précédente consistait à appeler la route depuis la console du
 * navigateur. Mauvaise pratique : le mot de passe reste dans l'historique des
 * DevTools et s'expose à un copier-coller involontaire. Un champ de formulaire
 * en `type="password"` ne laisse aucune trace de ce genre.
 *
 * CE QUE CET ÉCRAN NE FAIT JAMAIS
 * -------------------------------
 * - il n'affiche pas la valeur saisie (aucun bouton « révéler ») ;
 * - il ne la journalise nulle part — aucun `console.log`, jamais, même en
 *   développement ;
 * - il ne la place ni dans l'URL, ni dans le `localStorage`, ni dans un état
 *   persistant : les champs sont vidés dès la réponse reçue ;
 * - il n'accorde aucun privilège : ni accès Sentinel, ni clé API, ni rôle.
 *   La route sous-jacente n'écrit que `password_hash`.
 *
 * ORIGINE DE LA REQUÊTE
 * ---------------------
 * `getBackendUrl()` rend une chaîne vide en production : les appels sont donc
 * relatifs, donc de même origine. Le navigateur pose alors lui-même
 * `Sec-Fetch-Site: same-origin`, que le back-end exige (`@exiger_origine_sure`).
 * Cet en-tête est interdit à JavaScript, ce qui est précisément ce qui en fait
 * une défense CSRF : une page tierce ne peut pas le forger.
 */
export default function SecurityPasswordPage() {
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [preuve, setPreuve] = useState("");        // step-up, mode CHANGE seulement
  const [preuveRequise, setPreuveRequise] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState(null);    // { type: "ok" | "ko", texte }

  const MIN = 14;
  const tropCourt = motDePasse.length > 0 && motDePasse.length < MIN;
  const discordance = confirmation.length > 0 && motDePasse !== confirmation;
  const soumissionPossible =
    motDePasse.length >= MIN && motDePasse === confirmation && !envoi;

  const viderChamps = () => {
    setMotDePasse("");
    setConfirmation("");
    setPreuve("");
  };

  const soumettre = async (e) => {
    e.preventDefault();
    if (!soumissionPossible) return;

    setEnvoi(true);
    setMessage(null);

    // Le corps est construit ici et n'est référencé nulle part ailleurs.
    const corps = { new_password: motDePasse };
    if (preuve) {
      // `verify_stepup_auth` accepte l'un ou l'autre ; on laisse le back-end
      // trancher selon les facteurs réellement posés sur le compte.
      corps.password = preuve;
      corps.totp_code = preuve;
    }

    try {
      const res = await fetch(`${getBackendUrl()}/api/auth/password/bootstrap`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corps),
      });
      const data = await res.json().catch(() => ({}));

      // Les champs sont vidés AVANT tout rendu du résultat : la valeur ne
      // survit pas à la requête, quelle qu'en soit l'issue.
      viderChamps();

      if (res.ok && data.status === "success") {
        setPreuveRequise(false);
        setMessage({
          type: "ok",
          texte:
            "Facteur local enregistré. Vous pouvez maintenant activer la " +
            "double authentification depuis le portail.",
        });
      } else {
        // Un mot de passe existe déjà : le remplacer exige une preuve.
        const besoinPreuve = ["STEPUP_REQUIRED", "TOTP_REQUIRED", "INVALID_TOTP",
                              "INVALID_PASSWORD"].includes(data.code);
        setPreuveRequise(besoinPreuve);
        setMessage({
          type: "ko",
          texte: besoinPreuve
            ? "Un facteur existe déjà sur ce compte. Fournissez votre mot de " +
              "passe actuel ou un code d'authentification pour le remplacer."
            : "L'enregistrement n'a pas abouti.",
        });
      }
    } catch {
      viderChamps();
      setMessage({ type: "ko", texte: "L'enregistrement n'a pas abouti." });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={S.page}>
      <form style={S.carte} onSubmit={soumettre} autoComplete="off">
        <h1 style={S.titre}>Facteur local de sécurité</h1>
        <p style={S.intro}>
          Ce mot de passe ne remplace pas la connexion Google : il sert
          uniquement à confirmer les actions critiques et à récupérer l'accès.
        </p>

        <label style={S.label} htmlFor="mdp">
          Nouveau mot de passe ({MIN} caractères minimum)
        </label>
        <input
          id="mdp"
          type="password"
          autoComplete="new-password"
          style={S.champ}
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          disabled={envoi}
        />
        {tropCourt && (
          <p style={S.aide}>{MIN - motDePasse.length} caractère(s) manquant(s).</p>
        )}

        <label style={S.label} htmlFor="confirmation">Confirmation</label>
        <input
          id="confirmation"
          type="password"
          autoComplete="new-password"
          style={S.champ}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          disabled={envoi}
        />
        {discordance && <p style={S.aide}>Les deux saisies diffèrent.</p>}

        {preuveRequise && (
          <>
            <label style={S.label} htmlFor="preuve">
              Mot de passe actuel ou code d'authentification
            </label>
            <input
              id="preuve"
              type="password"
              autoComplete="off"
              style={S.champ}
              value={preuve}
              onChange={(e) => setPreuve(e.target.value)}
              disabled={envoi}
            />
          </>
        )}

        <button type="submit" style={{ ...S.bouton, opacity: soumissionPossible ? 1 : 0.4 }}
                disabled={!soumissionPossible}>
          {envoi ? "Enregistrement…" : "Enregistrer"}
        </button>

        {message && (
          <p style={{ ...S.message, color: message.type === "ok" ? "#0f0" : "#f66" }}>
            {message.texte}
          </p>
        )}
      </form>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", display: "flex", justifyContent: "center",
          alignItems: "center", background: "#000", padding: "1rem" },
  carte: { width: "100%", maxWidth: 420, background: "#0a0a0a",
           border: "1px solid #0ff3", borderRadius: 10, padding: "1.75rem",
           display: "flex", flexDirection: "column" },
  titre: { color: "#0ff", fontSize: "1.25rem", margin: "0 0 .5rem" },
  intro: { color: "#8ab", fontSize: ".85rem", lineHeight: 1.5, margin: "0 0 1.25rem" },
  label: { color: "#cde", fontSize: ".8rem", marginBottom: ".35rem" },
  champ: { background: "#111", border: "1px solid #234", borderRadius: 6,
           color: "#eee", padding: ".6rem .7rem", marginBottom: ".9rem",
           fontSize: "1rem", width: "100%", boxSizing: "border-box" },
  aide: { color: "#fa4", fontSize: ".78rem", margin: "-.6rem 0 .9rem" },
  bouton: { background: "#0ff", color: "#000", border: "none", borderRadius: 6,
            padding: ".7rem", fontSize: "1rem", fontWeight: 600,
            cursor: "pointer", marginTop: ".25rem" },
  message: { fontSize: ".85rem", marginTop: "1rem", lineHeight: 1.5 },
};
